import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Spinner, Tabs, Tab } from 'react-bootstrap';
import Filter from './components/filter/filter';
import ReportContainer from './components/report-container/report-container';
import AuditConfirmationContainer from './components/audit-confirmation-container/audit-confirmation-container';
import CertificationOfHoldingsContainer from './components/certification-of-holdings-container/certification-of-holdings-container';
import { useHistory } from 'react-router-dom';
import ReportsService from '../../../../services/reports-service';
import './checker-reports-module.scss';
import { IdentityContext } from '../../../../context/identity-context';

const CheckerReportsModule = () => {
  const reportsService = useMemo(() => new ReportsService(), []);
  const history = useHistory();
  const { user } = useContext(IdentityContext);

  // Raw data and filtered states
  const [items, setItems] = useState([]);
  const [auditConfirmations, setAuditConfirmations] = useState([]);
  const [certificationHoldings, setCertificationHoldings] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [filteredAuditConfirmations, setFilteredAuditConfirmations] = useState([]);
  const [filteredCertificationHoldings, setFilteredCertificationHoldings] = useState([]);

  // UI states
  const [activeTab, setActiveTab] = useState('outstanding');
  const [selectedReports, setSelectedReports] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Shared filter state for Filter component
  const [filterState, setFilterState] = useState({ status: [], issuers: [] });

  // Load data once
  useEffect(() => {
    let isMounted = true;
    const getReports = async () => {
      try {
        const result = await reportsService.getReports('rr_checker', user.attributes);
        if (!isMounted) return;
        const { adhocReports = [], consolidatedReports = [], certificationReports = [] } = result;
        setItems(adhocReports);
        setFilteredItems(adhocReports);
        setAuditConfirmations(consolidatedReports);
        setFilteredAuditConfirmations(consolidatedReports);
        setCertificationHoldings(certificationReports);
        setFilteredCertificationHoldings(certificationReports);
      } catch (error) {
        console.error('Fetching error:', error);
        history.push('/internal-server-error');
      } finally {
        isMounted && setLoading(false);
      }
    };
    getReports();
    return () => { isMounted = false; };
  }, [reportsService, user.attributes, history]);

  // Common filter application
  const applyFilters = useCallback((tab, filters) => {
    const { status, issuers } = filters;
    if (tab === 'outstanding') {
      setFilteredItems(
        items.filter(item =>
          (status.length === 0 || status.includes(item.status)) &&
          (issuers.length === 0 || issuers.includes(item.issuer))
        )
      );
    } else if (tab === 'audit') {
      setFilteredAuditConfirmations(
        auditConfirmations.filter(conf =>
          (status.length === 0 || status.includes(conf.status)) &&
          (issuers.length === 0 || issuers.includes(conf.issuerName))
        )
      );
    } else if (tab === 'certification') {
      setFilteredCertificationHoldings(
        certificationHoldings.filter(cert =>
          (status.length === 0 || status.includes(cert.status)) &&
          (issuers.length === 0 || issuers.includes(cert.bpId))
        )
      );
    }
  }, [items, auditConfirmations, certificationHoldings]);

  // Handler for Filter component updates
  const handleFilterChange = (newFilters) => {
    setFilterState(newFilters);
    applyFilters(activeTab, newFilters);
  };

  // Determine source data and filters based on active tab
  const [sourceData, currentFilters] = useMemo(() => {
    switch (activeTab) {
      case 'audit':
        return [auditConfirmations, filterState];
      case 'certification':
        return [certificationHoldings, filterState];
      default:
        return [items, filterState];
    }
  }, [activeTab, items, auditConfirmations, certificationHoldings, filterState]);

  // Normalize items for Filter component
  const normalizedItems = useMemo(() =>
    sourceData.map(item => {
      console.log(item);
      switch (activeTab) {
        case 'audit':
          return { status: item.status, issuer: item.issuerName };
        case 'certification':
          return { status: item.status, bpId: item.bpId };
        default:
          return { status: item.status, issuer: item.issuer };
      }
    }), [sourceData, activeTab]
  );

  // Define filter fields
  const filterFields = useMemo(() => [
    { key: 'status', label: 'Status', type: 'checkbox' },
    { key: 'issuer', label: 'Issuer', type: 'checkbox' }
  ], []);

  // Checkbox selection for outstanding
  const handleCheckboxChange = useCallback((id) => {
    setSelectedReports(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  return (
    <Container fluid className="checker-reports-module-container">
      {loading ? (
        <div className="loading-container">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <>
          <Tabs
            activeKey={activeTab}
            onSelect={tab => {
              setActiveTab(tab);
              // reset filters on tab change
              const reset = { status: [], issuers: [] };
              setFilterState(reset);
              applyFilters(tab, reset);
            }}
            className="mb-3"
          >
            <Tab eventKey="outstanding" title="Outstanding Balances" />
            <Tab eventKey="audit" title="Audit Confirmation" />
            <Tab eventKey="certification" title="Certification of Holdings" />
          </Tabs>
          <Row>
            <Col xs={12} md={3} className="panel">
              <Filter
                key={activeTab}
                items={normalizedItems}
                filter={currentFilters}
                onFilterChange={handleFilterChange}
                fields={filterFields}
              />
            </Col>
            <Col xs={12} md={9}>
              {activeTab === 'outstanding' && (
                <ReportContainer
                  filteredReports={filteredItems}
                  handleCheckboxChange={handleCheckboxChange}
                  selectedReports={selectedReports}
                />
              )}
              {activeTab === 'audit' && (
                <AuditConfirmationContainer items={filteredAuditConfirmations} />
              )}
              {activeTab === 'certification' && (
                <CertificationOfHoldingsContainer items={filteredCertificationHoldings} />
              )}
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default CheckerReportsModule;